import { useForm, FormProvider } from 'react-hook-form'
import styles from './ReferralForm.module.scss'
import { useTranslation } from 'next-i18next/pages'
import { Button, TextField, Form } from '@boltenergy-be/design-system'
import { Email } from 'components/inputs'
import { routes } from 'utils/routes'
import { useState } from 'react'
import parse from 'html-react-parser'
import ShareButtons from 'features/referral/components/share-buttons/ShareButtons'
import { useStoreSelector } from 'hooks/store'
import { ReferralFormInputs, ReferralFormProps } from 'features/referral/components/referral-form/types'
import { stringify } from 'qs'
import { getBoltWebsiteOrigin } from 'utils/helpers'
import axios from 'axios'
import { GetReferralCodeResponse, NextApiResponseBody } from 'types/bolt-api/request'
import { setPostHogPersonProperties, trackEvent } from 'utils/tracking'
import { useGetLanguage } from 'hooks/useGetLanguage'

const ReferralForm = ({ activeAction }: ReferralFormProps) => {
  // Redux
  const { sfUserAlias } = useStoreSelector((store) => store.marketing)

  // Local state
  const [referralLink, setReferralLink] = useState<string>('')
  const [noEmailFound, setNoEmailFound] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [linkCopied, setLinkCopied] = useState<boolean>(false)

  // I18N
  const { t } = useTranslation(['referral', 'common', 'commonFormFields', 'validation'])
  const language = useGetLanguage()

  // React Form
  const hookForm = useForm<ReferralFormInputs>()
  const { reset: formReset, handleSubmit } = hookForm

  /**
   * Checks is the email is from a bolt client
   * and sets the referralLink created for that user
   *
   * @param {ReferralFormInputs} data
   */
  const onSubmit = async (data: ReferralFormInputs) => {
    setLoading(true)

    setPostHogPersonProperties({ email: data.email.toLowerCase() })

    try {
      const query = stringify({ email: data.email, sfUserAlias }, { addQueryPrefix: true, skipNulls: true })
      const response = await axios.get<NextApiResponseBody<GetReferralCodeResponse>>(`/api/customers/referral-code${query}`)
      const code = response.data.data?.code

      if (code) {
        const searchQuery = stringify(
          { referralcode: code, sfu: sfUserAlias, utm_medium: 'referral', utm_source: 'community' },
          { addQueryPrefix: true, skipNulls: true }
        )
        setReferralLink(`${getBoltWebsiteOrigin()}${routes(language).referral}${searchQuery}`)
        trackEvent({ category: 'referral', object: 'link', action: 'generate' })
      } else {
        setNoEmailFound(true)
      }
    } catch (_) {
      setNoEmailFound(true)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Resets the form to its initial state
   */
  const handleReset = () => {
    formReset()
    setNoEmailFound(false)
    setLoading(false)
  }

  /**
   * Copies the referralLink to the users clipboard
   */
  const copyToClipboard = () => {
    setLinkCopied(true)
    trackEvent({ category: 'referral', object: 'link', action: 'copy' })

    if (navigator.clipboard) {
      void navigator.clipboard.writeText(referralLink)
      return
    }

    const textArea = document.createElement('textarea')
    textArea.value = referralLink
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }

  if (noEmailFound) {
    return (
      <div className="flex-col gap-500">
        <p className="weight-semibold">{parse(t('referrer.form.errorNoEmailFound'))}</p>
        <Button onClick={handleReset}>{t('referrer.form.buttonTryAgain')}</Button>
      </div>
    )
  }

  // referralLink
  if (referralLink) {
    return (
      <Form className={styles.form}>
        <TextField
          hiddenLabel
          placeholder={t('referrer.form.linkLabel')}
          label={t('referrer.form.linkLabel')}
          id="link"
          readOnly
          value={referralLink}
          grid="full"
          suffix={{
            as: (
              <Button onClick={copyToClipboard} size="small" type="button">
                {t(`referrer.form.${linkCopied ? 'copied' : 'copy'}`)}
              </Button>
            )
          }}
        />

        <div className={styles['link-card-share']}>
          <strong className={styles.share}>{t('referrer.form.share')}</strong>
          <ShareButtons {...{ activeAction, referralLink }} />
        </div>
      </Form>
    )
  }

  // Email Verification
  return (
    <FormProvider {...hookForm}>
      <Form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Email required={true} grid="full" hiddenLabel />

        <Button className="grid-col-full" loading={loading}>
          {t('referrer.form.generate')}
        </Button>
      </Form>
    </FormProvider>
  )
}

export default ReferralForm
